import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { notificationService } from '../../services/loanService';


const auditorLinks = [
  { to: '/admin/audit-logs', icon: '◉', label: 'Audit Dashboard', highlight: true },
];

const makerLinks = [
  { to: '/admin/maker',        icon: '◈', label: 'Maker Dashboard' },
  { to: '/admin/applications', icon: '⬡', label: 'Applications', highlight: true },
  { to: '/admin/approvals',    icon: '⚖', label: 'My Submissions' },
  { to: '/admin/users',        icon: '○', label: 'Users' },
];

const checkerLinks = [
  { to: '/admin/checker',      icon: '◈', label: 'Checker Dashboard' },
  { to: '/admin/approvals',    icon: '⚖', label: 'Approval Queue', highlight: true },
  { to: '/admin/applications', icon: '⬡', label: 'Applications' },
  { to: '/admin/users',        icon: '○', label: 'Users' },
];

const kycOfficerLinks = [
  { to: '/admin/kyc-officer',  icon: '✓', label: 'KYC Dashboard', highlight: true },
  { to: '/admin/users',        icon: '○', label: 'Users' },
];

const userLinks = [
  { to: '/dashboard',      icon: '◈', label: 'Dashboard' },
  { to: '/loans',          icon: '⬡', label: 'My Loans' },
  { to: '/loans/credit-check', icon: '+', label: 'Apply for Loan', highlight: true },
  { to: '/contributions',  icon: '💰', label: 'My Savings Plans' },
  { to: '/groups',         icon: '👥', label: 'My Groups' },
  { to: '/credit-report',  icon: '📊', label: 'Credit Report' },
  { to: '/documents',      icon: '◻', label: 'Documents' },
  { to: '/notifications',  icon: '🔔', label: 'Notifications' },
  { to: '/profile',        icon: '○', label: 'Profile & KYC' },
  { to: '/support',        icon: '◎', label: 'Support' },
  { to: '/settings',       icon: '⚙', label: 'Settings' },
];

const adminLinks = [
  { to: '/admin/dashboard',    icon: '◈', label: 'Dashboard' },
  { to: '/admin/approvals',    icon: '⚖', label: 'Approval Queue', highlight: true },
  { to: '/admin/applications', icon: '⬡', label: 'Applications' },
  { to: '/admin/users',        icon: '○', label: 'Users' },
  { to: '/admin/kyc',          icon: '✓', label: 'KYC Review' },
  { to: '/admin/disbursements',icon: '₦', label: 'Disbursements' },
  { to: '/admin/fraud',        icon: '⚠', label: 'Fraud & Compliance' },
  { to: '/admin/roles',        icon: '◻', label: 'Role Management' },
  { to: '/admin/audit-logs',     icon: '◉', label: 'Audit Logs' },
  { to: '/admin/loan-products',  icon: '⬡', label: 'Loan Products' },
  { to: '/admin/contribution-plans', icon: '💰', label: 'Savings Plans' },
  { to: '/admin/groups',         icon: '👥', label: 'Groups & Payouts' },
  { to: '/admin/coop-societies', icon: '🏛', label: 'Cooperative Societies' },
  { to: '/admin/analytics',      icon: '▲', label: 'Analytics' },
  { to: '/admin/reports',        icon: '▦', label: 'Reports' },
];

const disbursementOfficerLinks = [
  { to: '/admin/disbursement', icon: '₦', label: 'Disbursement Queue', highlight: true },
];

export default function Sidebar({ isOpen = false, onClose }) {
  const { isStaff, isChecker, isMaker, isAuditor, isDisbursementOfficer, isKycOfficer } = useAuth();
  const isPureChecker = isChecker && !isMaker;
  const isPureMaker   = isMaker && !isChecker;

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notif-unread-count'],
    queryFn: () => notificationService.getAll({ is_read: false, page_size: 1 }).then(r => r.data.unread_count || 0),
    refetchInterval: 30000,
    enabled: !isStaff,
  });

  const links = isStaff
    ? isAuditor             ? auditorLinks
    : isDisbursementOfficer ? disbursementOfficerLinks
    : isKycOfficer          ? kycOfficerLinks
    : isPureChecker         ? checkerLinks
    : isPureMaker           ? makerLinks
    : adminLinks
    : userLinks;
  const sectionLabel = isAuditor ? 'Auditor' : isDisbursementOfficer ? 'Disbursement' : isKycOfficer ? 'KYC' : isPureChecker ? 'Checker' : isPureMaker ? 'Maker' : isStaff ? 'Administration' : 'Navigation';

  return (
    <>
      <div className={`sidebar-backdrop${isOpen ? ' open' : ''}`} onClick={onClose} />
      <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`} style={{
      width: '264px',
      background: 'var(--sidebar-bg)',
      backdropFilter: 'blur(10px)',
      borderRight: '1px solid var(--border)',
      minHeight: 'calc(100vh - 60px)',
      padding: '1.25rem 0.75rem',
      display: 'flex', flexDirection: 'column', gap: '0.25rem',
    }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase', padding: '0 0.5rem', marginBottom: '0.5rem' }}>
        {sectionLabel}
      </div>

      {links.map(({ to, icon, label, highlight }) => (
        <NavLink key={to} to={to} onClick={onClose} style={({ isActive }) => ({
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.625rem 0.875rem',
          borderRadius: '10px',
          textDecoration: 'none',
          fontSize: '0.875rem',
          fontWeight: isActive ? 600 : 400,
          color: isActive ? 'var(--sidebar-active-text)' : 'var(--text-2)',
          background: isActive
            ? 'linear-gradient(135deg, rgba(0,200,150,0.2), rgba(0,200,150,0.08))'
            : highlight ? 'rgba(0,200,150,0.06)' : 'transparent',
          border: isActive
            ? '1px solid rgba(0,200,150,0.3)'
            : highlight ? '1px solid rgba(0,200,150,0.15)' : '1px solid transparent',
          transition: 'all .15s',
          position: 'relative',
          overflow: 'hidden',
        })}>
          {({ isActive }) => (
            <>
              {isActive && (
                <span style={{
                  position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 3,
                  background: 'var(--primary)', borderRadius: '0 4px 4px 0',
                  boxShadow: '0 0 8px var(--primary)',
                }} />
              )}
              <span style={{
                fontSize: '0.75rem',
                color: isActive ? 'var(--primary)' : highlight ? 'var(--primary)' : 'var(--text-3)',
                fontWeight: 700, width: 16, textAlign: 'center', flexShrink: 0,
              }}>
                {icon}
              </span>
              {label}
              {to === '/notifications' && unreadCount > 0 && (
                <span style={{
                  marginLeft: 'auto', fontSize: '0.6rem', fontWeight: 700,
                  color: '#fff', background: '#FF4757',
                  padding: '0.1rem 0.45rem', borderRadius: '99px',
                  boxShadow: '0 0 6px rgba(255,71,87,0.5)',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
              {highlight && !isActive && to !== '/notifications' && (
                <span style={{
                  marginLeft: 'auto', fontSize: '0.6rem', fontWeight: 700,
                  color: 'var(--primary)', background: 'var(--primary-light)',
                  padding: '0.1rem 0.4rem', borderRadius: '99px',
                }}>NEW</span>
              )}
            </>
          )}
        </NavLink>
      ))}

      {/* Bottom section */}
      <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
        <div style={{ padding: '0.875rem', background: 'var(--primary-light)', borderRadius: '10px', border: '1px solid rgba(0,200,150,0.2)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            SUPPORT
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-2)' }}>
            Need help? Contact us at support@mypcredit.com
          </div>
        </div>
      </div>
      </aside>
    </>
  );
}
